import { isNotBlank } from "@freightsimple/shared/Helpers/isNotBlank";
import type { UUID } from "@/Shared/types";
import { useAuthentication } from "@/Slices/authenticationSlice";
import { downloadBase64File } from "@/Helpers/downloadBase64File";
import { fileNameForDocumentType } from "@/Helpers/fileNameForDocumentType";
import { useTrackingApi } from "@/Hooks/useTrackingApi";
import type { Document } from "@freightsimple/generated-dashboard-openapi-client/models/Document";
import type { DocumentType } from "@freightsimple/generated-dashboard-openapi-client/models/DocumentType";
import type { GetTrackingDetails } from "@freightsimple/generated-dashboard-openapi-client/models/GetTrackingDetails";
import type { ShipmentForTracking } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentForTracking";
import type { TrackingGroupShipment } from "@freightsimple/generated-dashboard-openapi-client/models/TrackingGroupShipment";
import type { TrackingInfo } from "@freightsimple/generated-dashboard-openapi-client/models/TrackingInfo";

interface TrackingService {
  getShipmentForTracking: (trackingId: UUID) => Promise<ShipmentForTracking>;
  getTracking: (trackingId: UUID) => Promise<TrackingInfo>;
  getTrackingIdForProBolNumber: (
    proNumber: string,
  ) => Promise<UUID | undefined>;
  getDocumentsForTracking: (trackingId: UUID) => Promise<Document[]>;
  downloadPickupPackage: (trackingId: UUID) => Promise<void>;
  downloadDocument: (
    tId: UUID,
    documentId: UUID,
    documentType: DocumentType,
  ) => Promise<void>;
  getTrackingDetails: (tId: UUID) => Promise<GetTrackingDetails>;
  getTrackingGroup: (packedGroup: string) => Promise<TrackingGroupShipment[]>;
}

export function useTrackingService(): TrackingService {
  const trackingApi = useTrackingApi();
  const { accessToken } = useAuthentication();

  return {
    getTrackingIdForProBolNumber: async function (
      proBolNumber: string,
    ): Promise<UUID | undefined> {
      const response = await trackingApi.getTrackingIdForProBolNumber({
        proBolNumber,
      });
      return response.trId;
    },

    getShipmentForTracking: async function (
      tId: UUID,
    ): Promise<ShipmentForTracking> {
      const response = await trackingApi.getShipmentForTracking({
        tId,
      });

      return response;
    },

    getTracking: async function (tId: UUID): Promise<TrackingInfo> {
      const response = await trackingApi.getTrackingByTId({
        tId,
      });

      return response;
    },

    getDocumentsForTracking: async function (tId: UUID): Promise<Document[]> {
      const response = await trackingApi.listDocumentsForTracking({
        tId,
      });
      return response.documents;
    },

    downloadPickupPackage: async function (tId: UUID) {
      const pickupPackage = await trackingApi.getTrackingPickupPackagePdf({
        tId,
      });

      downloadBase64File(
        pickupPackage.base64EncodedPdf,
        "pickupPackage.pdf",
        "application/pdf",
      );
    },

    downloadDocument: async function (
      tId: UUID,
      documentId: UUID,
      documentType: DocumentType,
    ) {
      const response = await trackingApi.getTrackingDocumentPdf({
        tId,
        documentId,
      });

      downloadBase64File(
        response.base64EncodedPdf,
        fileNameForDocumentType(documentType),
        "application/pdf",
      );
    },

    getTrackingDetails: async function (tId: UUID) {
      if (isNotBlank(accessToken)) {
        return await trackingApi.getTrackingDetails(
          { tId },
          {
            headers: {
              ["Authorization"]: `Bearer ${accessToken}`,
            },
          },
        );
      }

      return await trackingApi.getTrackingDetails({ tId });
    },

    getTrackingGroup: async function (
      packedGroup: string,
    ): Promise<TrackingGroupShipment[]> {
      const response = await trackingApi.getTrackingGroup({ d: packedGroup });

      return response.shipments;
    },
  };
}
